import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '@/components/banker-recon/types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toLocalDateString } from '@/utils/dateHelpers';
import { createComponentLogger } from '@/utils/logger';
import {
  type ReverseServicingPaymentInput,
  type ServicingPayment,
  type ServicingPaymentReversalReason,
  toNumber
} from '@/types/servicing';

const log = createComponentLogger('ReversePaymentDialog', 'servicing');

const REVERSAL_REASONS: Array<{ value: ServicingPaymentReversalReason; label: string }> = [
  { value: 'nsf', label: 'NSF / bounced check' },
  { value: 'chargeback', label: 'Chargeback' },
  { value: 'bank_reject', label: 'Bank rejected payment' },
  { value: 'borrower_revoked', label: 'Borrower revoked payment' },
  { value: 'other', label: 'Other' }
];

interface ReversePaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: ServicingPayment | null;
  onSuccess: () => Promise<void>;
  handleReversePayment: (input: ReverseServicingPaymentInput) => Promise<{ success: boolean; error?: string }>;
  userId?: string;
}

export function ReversePaymentDialog({
  open,
  onOpenChange,
  payment,
  onSuccess,
  handleReversePayment,
  userId
}: ReversePaymentDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [reversedAt, setReversedAt] = useState(toLocalDateString());
  const [reason, setReason] = useState<ServicingPaymentReversalReason>('nsf');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) {
return;
}
    setSubmitting(false);
    setReversedAt(toLocalDateString());
    setReason('nsf');
    setNotes('');
  }, [open, payment?.id]);

  const receivedSummary = useMemo(() => {
    if (!payment) {
return null;
}
    return {
      amount: formatCurrency(toNumber(payment.total_amount_received)),
      date: payment.payment_received_date || '--',
      method: payment.payment_method || '--',
      reference: payment.reference_number || '--'
    };
  }, [payment]);

  const handleSubmit = async () => {
    if (!payment) {
return;
}
    if (!reversedAt) {
      toast.error('Missing reversal date', { description: 'Please enter the date the payment bounced or was reversed.' });
      return;
    }

    setSubmitting(true);
    const input: ReverseServicingPaymentInput = {
      servicing_loan_id: payment.servicing_loan_id,
      payment_number: payment.payment_number,
      reversed_at: reversedAt,
      reversal_reason: reason,
      reversal_notes: notes || undefined,
      reversed_by: userId
    };

    try {
      const result = await handleReversePayment(input);
      if (result.success) {
        toast.success('Payment marked as bounced', {
          description: `Payment #${payment.payment_number} is now unpaid again and can be re-recorded.`
        });
        await onSuccess();
      } else {
        toast.error('Unable to reverse payment', { description: result.error || 'An unexpected error occurred.' });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      log.error('Failed to reverse payment', { paymentId: payment.id }, error as Error);
      toast.error('Unable to reverse payment', { description: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Mark Payment Bounced</DialogTitle>
          <DialogDescription>
            This clears the current receipt and reopens the scheduled payment so the replacement can be recorded on the same payment number.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-md border bg-muted/30 p-3 text-sm">
            <div className="font-medium">
              Payment #{payment?.payment_number ?? '--'} currently received for {receivedSummary?.amount ?? '--'}
            </div>
            <div className="mt-1 text-muted-foreground">
              Received {receivedSummary?.date ?? '--'} via {receivedSummary?.method ?? '--'} • Ref {receivedSummary?.reference ?? '--'}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="reversed-at">Bounce / Reversal Date</Label>
              <Input
                id="reversed-at"
                type="date"
                value={reversedAt}
                onChange={(event) => setReversedAt(event.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reversal-reason">Reason</Label>
              <Select value={reason} onValueChange={(value) => setReason(value as ServicingPaymentReversalReason)}>
                <SelectTrigger id="reversal-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {REVERSAL_REASONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reversal-notes">Notes</Label>
            <Textarea
              id="reversal-notes"
              rows={3}
              placeholder="Optional notes about the bounce or reversal..."
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !payment}>
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Mark Bounced'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
